/**
 * @fileoverview ListGroupedView — PlaceId-grouped list for the **List** tab.
 *
 * Consumes `MarketVenueMappings` records from the nearest react-admin
 * `ListContextProvider` and presents them as one expandable row per unique
 * `placeId` value (records without a `placeId` are collected into a single
 * **"(no place ID)"** group at the bottom).
 *
 * All groups are **expanded by default** so users can immediately see all
 * mappings without additional clicks.
 *
 * Each group row shows: Unified Name (clickable → {@link UnifiedNameDrawer}),
 * Location (City, State + Address).
 * Expanding a group reveals all market venue mappings sharing that PlaceId:
 * Market, Venue ID, Venue, Inserted On, Updated On, Updated By.
 *
 * There are **no checkboxes or bulk actions** in this view.
 */

import * as React from 'react';
import { useListContext } from 'react-admin';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Box,
    Typography,
} from '@mui/material';
import type { MarketVenueMappingRecord } from '../utils';
import UnifiedNameDrawer from './UnifiedNameDrawer';
import ListGroupRow, { LIST_COL_WIDTHS, type PlaceIdGroup } from './ListGroupRow';

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

/**
 * Groups a flat array of {@link MarketVenueMappingRecord}s by their `placeId`.
 *
 * - Records whose `placeId` is falsy are placed in a single group keyed by `''`.
 * - Named groups are sorted alphabetically; the empty group is always last.
 *
 * @param data - Array of records from react-admin's list context.
 * @returns Ordered array of {@link PlaceIdGroup} objects.
 */
function buildPlaceIdGroups (data: MarketVenueMappingRecord[] | undefined): PlaceIdGroup[] {
    const map = new Map<string, MarketVenueMappingRecord[]>();

    (data ?? []).forEach(record => {
        const key = record.placeId || '';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(record);
    });

    const entries = Array.from(map.entries());
    entries.sort(([a], [b]) => {
        if (a === '' && b !== '') return 1;
        if (a !== '' && b === '') return -1;
        return a.localeCompare(b);
    });

    return entries.map(([placeId, records]) => ({ placeId, records }));
}

// ---------------------------------------------------------------------------
// ListGroupedView
// ---------------------------------------------------------------------------

/**
 * List tab component — groups `MarketVenueMappings` records by `placeId` and
 * supports row-expand to display all mappings per PlaceId.
 *
 * Reads data from the nearest react-admin `ListContextProvider` (provided by
 * the `RaList` wrapper in `index.tsx` with `resource="MarketVenueMappings"`).
 * Records are grouped client-side using {@link buildPlaceIdGroups}.
 *
 * All groups are expanded by default. Clicking a Unified Name in the group
 * row opens {@link UnifiedNameDrawer} with a Google Maps preview.
 *
 * ### Column layout (group rows)
 * - **Unified Name** — Google Places display name (clickable → map drawer).
 * - **Location** — City + State on one line; formatted address below.
 *
 * ### Column layout (expanded mapping rows)
 * - **Market** — Market label.
 * - **Venue ID** — Source venue identifier.
 * - **Venue** — Venue name + city.
 * - **Inserted On** — Creation timestamp.
 * - **Updated On** — Last-update timestamp.
 * - **Updated By** — Last updater email.
 */
const ListGroupedView: React.FC = () => {
    const { data } = useListContext<MarketVenueMappingRecord>();

    const groups = React.useMemo(() => buildPlaceIdGroups(data), [data]);

    /**
     * Filtered groups based on the current filter values. This is memoized to avoid unnecessary re-renders when the filter values haven't changed.
     * The filtering logic can be implemented here based on the filterValues.
     */
    const filteredGroups = React.useMemo(() => {
        return groups.filter(g => g.records.filter(r => r.id).length > 0);
    }, [groups]);

    /** Set of currently-expanded PlaceIds. */
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

    /**
     * Whenever the groups change (e.g. after a filter or page change):
     * expand all groups by default.
     */
    React.useEffect(() => {
        if (!filteredGroups.length) {
            setExpandedIds(new Set());
            return;
        }
        setExpandedIds(new Set(filteredGroups.map(g => g.placeId)));
    }, [filteredGroups]);

    // ── UnifiedNameDrawer state ───────────────────────────────────────────────
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [drawerVenue, setDrawerVenue] = React.useState<{
        unifiedName: string;
        formattedAddress: string;
        googleMapsUri: string;
    } | null>(null);

    /**
     * Opens the map preview drawer for the given venue.
     *
     * @param unifiedName      - Venue display name.
     * @param formattedAddress - Full formatted address.
     * @param googleMapsUri    - Google Maps URI.
     */
    const handleOpenDrawer = (
        unifiedName: string,
        formattedAddress: string,
        googleMapsUri: string,
    ): void => {
        setDrawerVenue({ unifiedName, formattedAddress, googleMapsUri });
        setDrawerOpen(true);
    };

    /**
     * Toggles the expanded state of a single PlaceId group.
     *
     * @param placeId - The group's key (empty string for the no-placeId group).
     */
    const handleToggle = (placeId: string): void => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(placeId)) {
                next.delete(placeId);
            } else {
                next.add(placeId);
            }
            return next;
        });
    };

    if (!filteredGroups.length) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    No venue mapping records found.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Table size="small" aria-label="list grouped by place ID" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: LIST_COL_WIDTHS.chevron }} />
                        <TableCell sx={{ fontWeight: 700, width: LIST_COL_WIDTHS.unifiedName }}>Unified Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: LIST_COL_WIDTHS.location }}>Location</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredGroups.map(group => (
                        <ListGroupRow
                            key={group.placeId === '' ? '__no_place_id__' : group.placeId}
                            group={group}
                            expanded={group.records.filter(r => r.id).length > 0 && expandedIds.has(group.placeId)}
                            onToggle={() => handleToggle(group.placeId)}
                            onOpenDrawer={handleOpenDrawer}
                        />
                    ))}
                </TableBody>
            </Table>

            {/* Map preview drawer */}
            <UnifiedNameDrawer
                open={drawerOpen}
                unifiedName={drawerVenue?.unifiedName ?? ''}
                formattedAddress={drawerVenue?.formattedAddress}
                googleMapsUri={drawerVenue?.googleMapsUri}
                onClose={() => setDrawerOpen(false)}
            />
        </>
    );
};

export default ListGroupedView;
